import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CheckCircleIcon, XCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import { apiClient } from '../../lib/api'
import { toast } from 'react-hot-toast'

export default function ActivatePage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<'activating' | 'success' | 'error'>('activating')
  const [errorMessage, setErrorMessage] = useState('')
  const token = searchParams.get('token')

  useEffect(() => {
    if (!token) {
      setStatus('error')
      setErrorMessage('Invalid activation link. Please check your email and try again.')
      return
    }

    activateAccount()
  }, [token])

  const activateAccount = async () => {
    if (!token) return

    try {
      setStatus('activating')
      await apiClient.activateAccount(token)
      setStatus('success')
      toast.success('Account activated successfully! 🎉')

      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/dashboard')
      }, 2000)
    } catch (error: any) {
      setStatus('error')
      const message = error.message || 'Failed to activate account. The link may have expired.'
      setErrorMessage(message)
      toast.error(message)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Redd Miner</h1>
            <p className="text-gray-600 mt-2">Account Activation</p>
          </div>

          {/* Content */}
          <div className="text-center">
            {status === 'activating' && (
              <div className="py-8">
                <ArrowPathIcon className="w-16 h-16 text-indigo-600 mx-auto animate-spin" />
                <h2 className="text-xl font-semibold text-gray-900 mt-4">Activating your account...</h2>
                <p className="text-gray-600 mt-2">Please wait while we verify your email address.</p>
              </div>
            )}

            {status === 'success' && (
              <div className="py-8">
                <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto" />
                <h2 className="text-xl font-semibold text-gray-900 mt-4">Account activated!</h2>
                <p className="text-gray-600 mt-2">
                  Your email has been verified successfully. Redirecting to dashboard...
                </p>
                <div className="mt-6">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-indigo-600 h-2 rounded-full animate-pulse" style={{ width: '100%' }}></div>
                  </div>
                </div>
              </div>
            )}

            {status === 'error' && (
              <div className="py-8">
                <XCircleIcon className="w-16 h-16 text-red-500 mx-auto" />
                <h2 className="text-xl font-semibold text-gray-900 mt-4">Activation failed</h2>
                <p className="text-gray-600 mt-2">{errorMessage}</p>
                <div className="mt-6 space-y-3">
                  <button
                    onClick={() => navigate('/login')}
                    className="w-full py-2 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors"
                  >
                    Go to Login
                  </button>
                  <button
                    onClick={() => window.location.reload()}
                    className="w-full py-2 px-4 bg-gray-200 hover:bg-gray-300 text-gray-900 font-medium rounded-lg transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
